import * as React from 'react';
import { useRecoilValue } from 'recoil';
import { useIntl } from 'react-intl';
import PermissionsService, {
  PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
  PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
} from '../../../Services/PermissionsService';
import {
  TABLE_ALARM,
  TABLE_AUTO_REPORT,
  TABLE_CORRECTIONS,
  TABLE_REFUELING,
  TABLE_REFUELINGS_IMPORT_RECORDS,
} from '../../../Services/TableService';
import ConfirmDialog from '../../ConfirmDialog';
import ReportService from '../../../Services/ReportService';
import AssignForm from '../../OnlinePanel/Form';
import ProfileAssignForm from '../../GdprUsersList/Form';
import { getAllowedMultipleActions } from '../TableService';
import ActionForm from '../../ImportRefuelings/ActionForm';
import { IGNORE, PENDING } from '../TableBody/TableRow';
import LoaderService from '../../../Services/LoaderService';
import { IDynamicTableState } from '../../../interfaces/tables';
import { IGroupSelectOptions } from '../../Forms/CmdField';
import { activateAlarmMultiple, deactivateAlarmMultiple } from '../../Alarms/AlarmService';
import { tablesAtom } from '../../../store/recoil/TableState';
import { postRequest, putRequest } from '../../../Services/HttpService';
import { AxiosResponse } from 'axios';

const RESPONSE_OK = 'OK';

interface IComponentProps {
  tableName: string;
  actionSave: any;
}

export default function MultipleActions(props: IComponentProps) {
  const tables = useRecoilValue<IDynamicTableState>(tablesAtom);
  const { formatMessage: f } = useIntl();
  const [allowedActions, setAllowedActions] = React.useState<any>([]);
  const [isImportFilesModalOpened, setIsImportFilesModalOpened] = React.useState<string>('');
  const [isButtonRemoveClicked, setIsButtonRemoveClicked] = React.useState<boolean>(false);
  const [isAssignModalOpened, setIsAssignModalOpened] = React.useState<boolean>(false);
  const [isDefaultAssignment, setIsDefaultAssignment] = React.useState<boolean>(false);
  const [isProfileModalForm, setIsProfileModalForm] = React.useState<boolean>(false);
  const [availableTemplates, setAvailableTemplates] = React.useState<IGroupSelectOptions | null>(
    null
  );

  const handleAllowedActions = React.useCallback(() => {
    const { tableName } = props;
    const { actions } = tables[tableName];
    const { multipleActions } = actions;
    const activeRows = tables[tableName].activeRows;
    const vehicleIdCheck: { [key: string]: '' } = {};
    const selectedCustomersRows: number[] = [];

    tables[tableName].records.forEach((item) => {
      if (item && item.vehicles) {
        item.vehicles.forEach((vehicle) => {
          vehicle.records.forEach((vehicleRecords) => {
            if (activeRows.includes(vehicleRecords.rideId)) {
              if (!vehicleIdCheck[vehicleRecords.vehicleId]) {
                vehicleIdCheck[vehicleRecords.vehicleId] = '';
              }
              vehicleIdCheck[vehicleRecords.vehicleId] = '';
            }
          });
        });
      }
      if (item && activeRows.includes(item.id) && item.customerId) {
        selectedCustomersRows.push(item.customerId);
      }
    });

    if (multipleActions && multipleActions.length > 0) {
      return getAllowedMultipleActions(
        multipleActions,
        tableName,
        activeRows,
        selectedCustomersRows,
        tableName === TABLE_REFUELINGS_IMPORT_RECORDS
          ? tables[TABLE_REFUELINGS_IMPORT_RECORDS].records
          : []
      );
    }
    return [];
  }, [props, tables]);

  React.useEffect(() => {
    setAllowedActions(handleAllowedActions());
  }, [handleAllowedActions]);

  const handleActivateAlarm = async (ids: string[]) => {
    LoaderService.showLoader();
    const response = await activateAlarmMultiple(ids);
    if (response && response.status === 'ok') {
      props.actionSave(null);
    }
    LoaderService.showLoader(false);
  };

  const handleDeactivateAlarm = async (ids: string[]) => {
    LoaderService.showLoader();
    const response = await deactivateAlarmMultiple(ids);
    if (response && response.status === 'ok') {
      props.actionSave(null);
    }
    LoaderService.showLoader(false);
  };

  const handleActivateReport = async (ids: string[]) => {
    LoaderService.showLoader();
    const response = await ReportService.activateReport(ids);
    if (response && response.status === 'ok') {
      props.actionSave(null);
    }
    LoaderService.showLoader(false);
  };

  const handleDeactivateReport = async (ids: string[]) => {
    LoaderService.showLoader();
    const response = await ReportService.deactivateReport(ids);
    if (response && response.status === 'ok') {
      props.actionSave(null);
    }
    LoaderService.showLoader(false);
  };

  const handleTemplateAssignmentModal = async (activeId: string) => {
    const { activeRows, records } = tables[props.tableName];
    const apiUrl = `/v1/online-map/assign/available-templates`;
    const inputs = {
      vehicleIds: activeRows,
      default: activeId === 'action_assignDefault',
    };
    LoaderService.showLoader();

    const selectedCustomersRows: number[] = [];

    records.forEach((item) => {
      if (item && activeRows.includes(item.id) && item.customerId) {
        selectedCustomersRows.push(item.customerId);
      }
    });

    const response: AxiosResponse | false = await postRequest(apiUrl, inputs, { returnAxiosResponse: true });
    if (response && response.data && response.status === 200) {
      const filteredTemplates = { ...response.data };
      const filteredSystemTemplates =
        filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.system' })] &&
        PermissionsService.hasAccess(
          PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
          ['read'],
          selectedCustomersRows
        )
          ? filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.system' })]
          : [];

      filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.system' })] = filteredSystemTemplates;

      const filteredCustomerTemplates =
        filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.public' })] &&
        PermissionsService.hasAccess(
          PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
          ['read'],
          selectedCustomersRows
        )
          ? filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.public' })]
          : [];

      filteredTemplates[f({ id: 'enum.onlineMapTemplate.type.public' })] =
        filteredCustomerTemplates;

      setIsAssignModalOpened(true);
      setAvailableTemplates(filteredTemplates);
      setIsDefaultAssignment(activeId === 'action_assignDefault');
    }
    LoaderService.showLoader(false);
  };

  const handleProfileAssigningForm = () => {
    setIsProfileModalForm(true);
  };

  const handleRemove = () => {
    setIsButtonRemoveClicked(true);
  };

  const handleConfirmCancel = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | KeyboardEvent
  ) => {
    event.preventDefault();
    setIsButtonRemoveClicked(false);
  };

  const remove = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    const { tableName, actionSave } = props;
    const { activeRows } = tables[tableName];
    LoaderService.showLoader();
    const link = `/v1/${tableName}/remove`;

    const customerIdsSelectedData: { [key: string]: number[] } = {};

    tables[tableName].records.map((item) => {
      if (activeRows.includes(item.id)) {
        if (!customerIdsSelectedData[item.customerId]) {
          customerIdsSelectedData[item.customerId] = [];
        }
        customerIdsSelectedData[item.customerId].push(item.id);
      }
    });

    const expenseImportRecordId = { expenseImportRecordId: customerIdsSelectedData };
    const data =
      tableName === TABLE_CORRECTIONS
        ? { correctionId: activeRows }
        : tableName === TABLE_ALARM
          ? { alarmId: activeRows }
          : tableName === TABLE_REFUELINGS_IMPORT_RECORDS
            ? expenseImportRecordId
            : { ids: customerIdsSelectedData };

    const response: AxiosResponse | false =
      tableName === TABLE_REFUELINGS_IMPORT_RECORDS
        ? await postRequest(link, expenseImportRecordId, { returnAxiosResponse: true })
        : await putRequest(link, data, { returnAxiosResponse: true });

    if (response && response.data && response.data.status === 'ok') {
      actionSave(null);
    }
    LoaderService.showLoader(false);
  };

  const handleConfirmRemove = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    remove(event);
    setIsButtonRemoveClicked(false);
  };

  const changeStatusRefueling = (statusType: string) => {
    if (statusType === PENDING) {
      setIsImportFilesModalOpened(PENDING);
    } else {
      setIsImportFilesModalOpened(IGNORE);
    }
  };

  const handleBulkOperations = (e: any) => {
    e.preventDefault();
    const activeId = e.currentTarget.id;
    const { tableName } = props;
    const { activeRows } = tables[props.tableName];

    if (activeId === 'action_remove') {
      handleRemove();
    } else if (activeId === 'action_activate') {
      if (tableName === TABLE_ALARM) {
        handleActivateAlarm(activeRows);
      } else if (tableName === TABLE_AUTO_REPORT) {
        handleActivateReport(activeRows);
      }
    } else if (activeId === 'action_deactivate') {
      if (tableName === TABLE_ALARM) {
        handleDeactivateAlarm(activeRows);
      } else if (tableName === TABLE_AUTO_REPORT) {
        handleDeactivateReport(activeRows);
      }
    } else if (activeId === 'action_assign') {
      handleTemplateAssignmentModal(activeId);
    } else if (activeId === 'action_assignDefault') {
      handleTemplateAssignmentModal(activeId);
    } else if (activeId === 'action_assign-profile') {
      handleProfileAssigningForm();
    } else if (activeId === 'action_status_pending' || activeId === 'action_status_ignore') {
      changeStatusRefueling(activeId);
    }
  };

  const handleCloseModal = () => {
    setIsAssignModalOpened(false);
    setIsProfileModalForm(false);
    setIsImportFilesModalOpened('');
  };

  const getCustomerLabel = () => {
    let customerLabel = '';
    let customerId = null;
    if (
      tables[props.tableName].columns.customerId &&
      tables[props.tableName].columns.customerId.filter
    ) {
      customerId = tables[props.tableName].columns.customerId.filter.selectedValue;
      const customerValues = tables[props.tableName].columns.customerId.filter.values;
      const customerData = customerValues.find((i: any) => String(i.value) === String(customerId));
      if (customerData) {
        customerLabel = customerData.item;
      }
    }
    return customerLabel;
  };

  // const getLockedRideBoolean = (locked: string, customerId: number, type: string) => {
  //   if (locked === 'locked') {
  //     return true;
  //   }
  //   if (locked === 'restricted') {
  //     return !PermissionsService.hasAccess(PERMISSION_PERIOD_LOCK_ADMIN, [type], customerId);
  //   }
  //   return false;
  // };

  const getLockedItemsBoolean = () => {
    const { tableName } = props;
    if (tableName === TABLE_REFUELING || tableName === TABLE_CORRECTIONS) {
      const { records } = tables[tableName];
      const activeRows = tables[tableName].activeRows;
      let canLocked = false;
      records.forEach((item: any) => {
        if (activeRows.includes(item.id) && item.locked === 'locked') {
          canLocked = true;
        }
      });
      return canLocked;
    }
    return false;
  };

  const getActiveRowsWithCustomers = () => {
    const { activeRows } = tables[props.tableName];
    const data = tables[props.tableName];
    const activeRowsWithCustomers = {};
    if (data) {
      data.records.forEach((i) => {
        if (activeRows.includes(i.id)) {
          activeRowsWithCustomers[i.customerId] = activeRowsWithCustomers[i.customerId]
            ? [...activeRowsWithCustomers[i.customerId], i.id]
            : [i.id];
        }
      });
    }
    return activeRowsWithCustomers;
  };

  const findIcon = (i: any) => {
    let icon = '';
    if (i.id === 'action_parking_edit_comment' || i.id === 'action_ride_edit_comment') {
      icon = 'cmd-icon cmd-icon-comment-add mr-xs fs-4';
    } else if (i.id === 'action_parking_add_purpose' || i.id === 'action_ride_add_purpose') {
      icon = 'cmd-icon cmd-icon-single-folded-content-add mr-xs fs-4';
    } else if (i.id === 'action_change_driver') {
      icon = 'cmd-icon cmd-icon-single-user mr-xs fs-4';
    } else if (i.id === 'action_display_ride') {
      icon = 'cmd-icon cmd-icon-play-simple mr-xs fs-4';
    } else if (i.id === 'action_change_type_business') {
      icon = 'cmd-icon cmd-icon-ride-mode-work mr-xs fs-4';
    } else if (i.id === 'action_activate') {
      icon = 'cmd-icon cmd-icon-active mr-xs fs-4';
    } else if (i.id === 'action_deactivate') {
      icon = 'cmd-icon cmd-icon-inactive mr-xs fs-4';
    } else if (i.id === 'action_change_type_private') {
      icon = 'cmd-icon cmd-icon-shifting-mode-private mr-xs fs-4';
    } else if (i.id === 'action_status_pending') {
      icon = 'cmd-icon cmd-icon-listing mr-xs fs-4';
    } else if (i.id === 'action_status_ignore') {
      icon = 'cmd-icon cmd-icon-ban mr-xs fs-4';
    } else if (i.id === 'action_assign-profile') {
      icon = 'cmd-icons cmd-icon-swich-profile mr-xs fs-4';
    } else if (i.id === 'action_remove') {
      icon = 'cmd-icon cmd-icon-bin mr-xs fs-4';
    } else if (i.id === 'action_assign') {
      icon = 'cmd-icon  cmd-icon-edit-template-person-alternative-2 mr-xs fs-4';
    } else if (i.id === 'action_assignDefault') {
      icon = 'cmd-icon cmd-icon-edit-template-alternative-2 mr-xs fs-4';
    } else {
      icon = `cmd-icon cmd-icon-${i.icon} mr-xs fs-4`;
    }
    return icon;
  };

  const updateTable = () => {
    props.actionSave(null);
  };

  const { activeRows } = tables[props.tableName];
  return (
    <div
      style={{ margin: '17px -12px', borderRadius: 0 }}
      className="bg-primary border-radius-xs px-2 py-xs"
    >
      {isImportFilesModalOpened && (
        <div style={{ color: '#494949' }}>
          <ActionForm
            handleClose={handleCloseModal}
            activeRows={getActiveRowsWithCustomers()}
            updateTable={updateTable}
            action={isImportFilesModalOpened}
            id={null}
          />
        </div>
      )}
      {isProfileModalForm && (
        <div style={{ color: '#494949' }}>
          <ProfileAssignForm
            closeCallback={handleCloseModal}
            userIds={activeRows}
            updateTable={updateTable}
            customerId={
              tables[props.tableName].columns.customerId &&
              tables[props.tableName].columns.customerId.filter
                ? tables[props.tableName].columns.customerId.filter.selectedValue
                : null
            }
            options={
              tables[props.tableName].columns &&
              tables[props.tableName].columns.profile &&
              tables[props.tableName].columns.profile.filter
                ? tables[props.tableName].columns.profile.filter.values
                : []
            }
            customerLabel={getCustomerLabel()}
          />
        </div>
      )}
      {isAssignModalOpened && availableTemplates && (
        <div style={{ color: '#494949' }}>
          <AssignForm
            availableTemplates={availableTemplates}
            closeCallback={handleCloseModal}
            vehicleIds={activeRows}
            updateTable={updateTable}
            default={isDefaultAssignment}
          />
        </div>
      )}
      {isButtonRemoveClicked && (
        <ConfirmDialog
          title={f({
            id: 'table.confirm.dialog.multiple_remove.title',
          })}
          cancelCallback={handleConfirmCancel}
          continueCallback={handleConfirmRemove}
          cancelButtonTitle={f({
            id: 'waypoints.cancel',
          })}
          continueButtonTitle={f({
            id: 'table.deleteBtn',
          })}
        >
          {getLockedItemsBoolean() && (
            <div>
              {f({
                id:
                  props.tableName === TABLE_REFUELING
                    ? 'periodLock.multiple_remove.refueling'
                    : 'periodLock.multiple_remove.correction',
              })}
              <br />
              <br />
            </div>
          )}
          {f({
            id: 'table.confirm.dialog.multiple_remove.message',
          })}
        </ConfirmDialog>
      )}
      <span className="position-relative top-2 mr-1">
        {f({ id: 'table.chosen' })}: {activeRows.length}
      </span>
      {activeRows.length !== 0 &&
        allowedActions.map((i: any) => (
          <a
            key={`allowedAction${i.id}`}
            href="#"
            // title={f({ id: i.title })}
            title={i.title}
            data-cy={`multiple-action-${i.id}`}
            className="e-button mr-xs my-xs"
            id={i.id}
            onClick={handleBulkOperations}
          >
            <i className={findIcon(i)} />
            {i.id === 'action_display_ride' ? f({ id: 'rides.showRides' }) : i.title}
          </a>
        ))}
    </div>
  );
}
